import { useState } from 'react';
import type { FC, FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MarketingLayout from '../components/MarketingLayout';
import { useTranslation } from '../i18n/I18nContext';
import { Spinner } from '../components/ui/spinner';

const AuthVerify: FC = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { refreshSession } = useAuth();

  const { email, password } = (location.state as { email?: string; password?: string }) || {};

  if (!email || !password) {
    return <Navigate to="/auth/email" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsVerifying(true);

    try {
      const { confirmSignUp, signIn } = await import('aws-amplify/auth');
      await confirmSignUp({ username: email, confirmationCode: code.trim() });

      // confirmSignUp may auto-sign-in; try signIn but tolerate "already signed in"
      try {
        await signIn({ username: email, password });
      } catch (signInErr: any) {
        // Ignore if already authenticated after confirmSignUp
        if (signInErr?.name !== 'UserAlreadyAuthenticatedException') {
          throw signInErr;
        }
      }

      await refreshSession();
      navigate('/articles', { replace: true });
    } catch (err: any) {
      const errorName = err?.name || '';

      if (errorName === 'CodeMismatchException') {
        setError(t('auth.invalid_code'));
      } else if (errorName === 'ExpiredCodeException') {
        setError(t('auth.code_expired'));
      } else {
        setError(err instanceof Error ? err.message : t('auth.error_generic'));
      }
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendMessage('');

    try {
      const { resendSignUpCode } = await import('aws-amplify/auth');
      await resendSignUpCode({ username: email });
      setResendMessage(t('auth.code_resent'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.error_generic'));
    }
  };

  return (
    <MarketingLayout>
      <div className="max-w-sm mx-auto px-4 pt-8 pb-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t('auth.verify_heading')}
          </h1>
          <p className="text-muted-foreground">
            {t('auth.verify_description', { email })}
          </p>
        </div>

        {error && (
          <div className="bg-destructive text-destructive-foreground rounded-md p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {resendMessage && (
          <div className="bg-success/10 text-success rounded-md p-3 mb-4 text-sm">
            {resendMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('auth.verify_code_label')}</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={isVerifying}
              required
              autoFocus
              autoComplete="one-time-code"
              placeholder="000000"
              className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground text-sm text-center text-2xl tracking-[0.3em] font-mono"
            />
          </div>

          <button
            type="submit"
            disabled={isVerifying || code.length < 6}
            className="w-full bg-success text-success-foreground px-6 py-3 rounded-lg text-base font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {isVerifying ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner size="sm" className="border-success-foreground" />
                {t('auth.verifying')}
              </span>
            ) : (
              t('auth.verify_button')
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={handleResend}
            disabled={isVerifying}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('auth.resend_code')}
          </button>
        </div>
      </div>
    </MarketingLayout>
  );
};

export default AuthVerify;
